import { useEffect, useRef, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import Spotlight from "./pages/Spotlight";
import Account from "./pages/Account";
import AdminInvites from "./pages/AdminInvites";
import AdminBugs from "./pages/AdminBugs";
import AdminPipeline from "./pages/AdminPipeline";
import AdminPipelineVideo from "./pages/AdminPipelineVideo";
import AdminBetaSubmissions, { AdminBetaSubmission } from "./pages/AdminBetaSubmissions";
import BetaQuestionnaire, { BetaSubmitted } from "./pages/Beta";
import HeldPage from "./pages/Held";
import LivePlayer from "./pages/LivePlayer";
import LiveSession from "./pages/LiveSession";
import LiveSessions from "./pages/LiveSessions";
import ClipsPage from "./pages/Clips";
import { BillingResult, Community, DevCheckout } from "./pages/Misc";
import PlansPage from "./pages/Plans";
import RosterPage from "./pages/Roster";
import SignIn from "./pages/SignIn";
import Upload from "./pages/Upload";
import VideoPage from "./pages/VideoPage";
import Videos from "./pages/Videos";
import YoutubeDone from "./pages/YoutubeDone";
import TeamsPage from "./pages/Teams";
import ClaimPage from "./pages/Claim";
import UserPage from "./pages/UserPage";
import UserClipsPage from "./pages/UserClips";
import { BugButton } from "./components/BugButton";
import { checkForNewBuild, reloadIfStale, watchForNewBuild } from "./lib/freshness";

function Private({ children }: { children: ReactNode }) {
  const { me, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="p tiny" style={{ paddingTop: 24 }}>Loading…</div>;
  if (!me) return <Navigate to="/signin" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

/**
 * A newer build deployed while this tab was open: the next page change loads
 * that page in full, with the new code (lib/freshness.ts). A check that ends
 * within 3 s of the page change reloads it then; a slower one waits for the
 * next change, since the user may have started on the page by then.
 */
function FreshBuild() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => watchForNewBuild(), []);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (reloadIfStale()) return;
    const asked = Date.now();
    void checkForNewBuild().then((stale) => {
      if (stale && Date.now() - asked < 3000) reloadIfStale();
    });
  }, [pathname]);
  return null;
}

/** Operators only: the beta questionnaire's preview (pages/Beta.tsx); anyone else goes home. */
function OperatorOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  if (me && !me.user.is_operator) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * A Player Spotlight beta participant has the questionnaire and the page after it, nothing else of
 * the app (the API refuses its other routes too): every other address comes back to the questionnaire.
 */
function AppRoutes() {
  const { me } = useAuth();
  if (me && me.user.access === "beta" && !me.user.is_operator) {
    return (
      <Routes>
        <Route path="/" element={<BetaQuestionnaire />} />
        <Route path="/spotlights/:id" element={<Spotlight />} />
        <Route path="/submitted" element={<BetaSubmitted />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/signin" element={<SignedOutOnly><SignIn /></SignedOutOnly>} />
      <Route path="/" element={<Private><Videos /></Private>} />
      <Route path="/upload" element={<Private><Upload /></Private>} />
      <Route path="/videos/:id" element={<Private><VideoPage /></Private>} />
      <Route path="/videos/:id/players" element={<Private><RosterPage /></Private>} />
      <Route path="/videos/:id/clips" element={<Private><ClipsPage /></Private>} />
      <Route path="/teams" element={<Private><TeamsPage /></Private>} />
      <Route path="/teams/:teamId" element={<Private><TeamsPage /></Private>} />
      <Route path="/claim/:token" element={<ClaimPage />} />
      <Route path="/u/:handle" element={<Private><UserPage /></Private>} />
      <Route path="/u/:handle/clips" element={<Private><UserClipsPage /></Private>} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/spotlights/:id" element={<Private><Spotlight /></Private>} />
      <Route path="/account" element={<Private><Account /></Private>} />
      <Route path="/held" element={<Private><HeldPage /></Private>} />
      <Route path="/admin/pipeline" element={<Private><AdminPipeline /></Private>} />
      <Route path="/admin/beta-submissions" element={<Private><OperatorOnly><AdminBetaSubmissions /></OperatorOnly></Private>} />
      <Route path="/admin/beta-submissions/:id" element={<Private><OperatorOnly><AdminBetaSubmission /></OperatorOnly></Private>} />
      <Route path="/admin/beta-submissions/:id/clips" element={<Private><OperatorOnly><ClipsPage betaReview /></OperatorOnly></Private>} />
      <Route path="/admin/pipeline/:id" element={<Private><AdminPipelineVideo /></Private>} />
      <Route path="/admin/invites" element={<Private><AdminInvites /></Private>} />
      <Route path="/admin/bugs" element={<Private><AdminBugs /></Private>} />
      <Route path="/admin/live" element={<Private><LiveSessions /></Private>} />
      <Route path="/admin/live/:id" element={<Private><LiveSession /></Private>} />
      <Route path="/admin/live/:id/players/:key" element={<Private><LivePlayer /></Private>} />
      <Route path="/beta" element={<Private><OperatorOnly><BetaQuestionnaire preview /></OperatorOnly></Private>} />
      <Route path="/beta/submitted" element={<Private><OperatorOnly><BetaSubmitted preview /></OperatorOnly></Private>} />
      <Route path="/youtube/done" element={<YoutubeDone />} />
      <Route path="/community" element={<Community />} />
      <Route path="/billing/success" element={<Private><BillingResult ok /></Private>} />
      <Route path="/billing/cancel" element={<Private><BillingResult ok={false} /></Private>} />
      <Route path="/billing/checkout-placeholder" element={<Private><DevCheckout /></Private>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function SignedOutOnly({ children }: { children: ReactNode }) {
  const { me, ready } = useAuth();
  if (!ready) return null;
  if (me) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <FreshBuild />
      <AuthProvider>
        <AppRoutes />
        <BugButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
